import CloseIcon from '@mui/icons-material/Close';
import FilterListIcon from '@mui/icons-material/FilterList';
import { Box, ClickAwayListener, IconButton, Paper, Popper } from '@mui/material';
import { useMemo, useState, type FC, type MouseEvent } from 'react';

import { filterColumnOptions } from 'helpers/dashboardCases';

export interface ColumnFilterOption {
    label: string;
    value: string;
}

interface ColumnTextFilterProps {
    value: string;
    onChange: (value: string) => void;
    placeholder: string;
    options?: ColumnFilterOption[];
    ariaLabel?: string;
}

const ColumnTextFilter: FC<ColumnTextFilterProps> = ({
    value,
    onChange,
    placeholder,
    options,
    ariaLabel,
}) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [query, setQuery] = useState('');
    const open = Boolean(anchorEl);
    const isOptionFilter = Boolean(options);
    const isActive = Boolean(value);
    const visibleOptions = useMemo(
        () => (options ? filterColumnOptions(options, query) : []),
        [options, query]
    );

    const handleToggle = (event: MouseEvent<HTMLElement>) => {
        const nextOpen = !anchorEl;
        setAnchorEl(nextOpen ? event.currentTarget : null);
        if (nextOpen) {
            setQuery('');
        }
    };

    const handleClear = () => {
        onChange('');
        setQuery('');
    };

    const handleInputChange = (nextValue: string) => {
        if (isOptionFilter) {
            setQuery(nextValue);
            return;
        }
        onChange(nextValue);
    };

    const handleSelect = (nextValue: string) => {
        onChange(nextValue);
        setQuery('');
        setAnchorEl(null);
    };

    return (
        <>
            <IconButton
                onClick={handleToggle}
                aria-label={ariaLabel ?? placeholder}
                sx={{
                    ml: 0.75,
                    bgcolor: isActive ? '#dddddd' : undefined,
                }}
            >
                <FilterListIcon />
            </IconButton>
            {isActive ? (
                <IconButton onClick={handleClear} sx={{ ml: 0.5 }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            ) : null}
            <Popper
                open={open}
                anchorEl={anchorEl}
                placement="right-start"
                modifiers={[
                    { name: 'offset', options: { offset: [0, 8] } },
                    { name: 'flip', options: { fallbackPlacements: ['left-start'] } },
                ]}
                style={{ zIndex: 1500 }}
            >
                <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
                    <Paper sx={{ p: 1 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                height: '100%',
                            }}
                        >
                            <input
                                type="text"
                                value={isOptionFilter ? query : value}
                                onChange={(event) => handleInputChange(event.target.value)}
                                placeholder={placeholder}
                                autoFocus
                                style={{
                                    height: '32px',
                                    borderRadius: '6px',
                                    border: '1px solid #d1d5db',
                                    padding: '0 28px 0 8px',
                                    fontSize: '12px',
                                    width: '200px',
                                }}
                            />
                            {(isOptionFilter ? query : value) ? (
                                <IconButton
                                    onClick={() => (isOptionFilter ? setQuery('') : handleClear())}
                                    sx={{ ml: '-28px' }}
                                >
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            ) : null}
                        </Box>
                        {isOptionFilter ? (
                            <Box
                                sx={{
                                    mt: 0.75,
                                    maxHeight: 220,
                                    overflowY: 'auto',
                                    width: 200,
                                }}
                            >
                                {visibleOptions.map((option) => {
                                    const selected = option.value === value;
                                    return (
                                        <Box
                                            key={option.value || 'empty'}
                                            onClick={() => handleSelect(option.value)}
                                            sx={{
                                                px: 1,
                                                py: 0.75,
                                                borderRadius: '6px',
                                                cursor: 'pointer',
                                                fontSize: 12,
                                                fontWeight: selected ? 700 : 400,
                                                '&:hover': { bgcolor: 'action.hover' },
                                            }}
                                        >
                                            {option.label}
                                        </Box>
                                    );
                                })}
                            </Box>
                        ) : null}
                    </Paper>
                </ClickAwayListener>
            </Popper>
        </>
    );
};

export default ColumnTextFilter;
